import * as Sentry from '@sentry/react';
import { cloneDeep, isArray, uniq } from 'lodash';
import { combineOriginIndex } from '@meitu/saas-editor-core/es/framework';
import { fixMarkupLayerSize } from '@meitu/saas-editor-core/es/layers/markup-layer';
import { getEmptyQrCodeLayerProps, parseQRCodeToText } from '@meitu/saas-editor-core/es/layers/qr-code-layer';
import { getCellStyle } from '@meitu/saas-editor-core/es/layers/table-layer/utils/table';
import { getBackgroundLayerProps } from '@meitu/saas-editor-core/es/utils/layer-kit';
import { TextMeasurer } from '@meitu/saas-editor-core/es/utils/text-kit';
import {
    transformFoundationVectorLayerDataToClient,
    transformLowLeverTextDataToCss,
} from '@meitu/saas-editor-core/es/utils/transform-kit';
import { asyncTraverseTrees, traverseTree, traverseTrees } from '@meitu/saas-editor-core/es/utils/tree-kit';
import { DEFAULT_DPI, SizeUnitType } from '@meitu/saas-editor-core/es/utils/unit-kit';
import {
    formatImageTransformToServer,
    getDefaultClientBackgroundValue,
    IServerLayerProps,
    parseServerLayersToClient,
} from '@meitu/saas-editor-core/es/version-parse';
import {
    Cell,
    DEFAULT_FONT_FAMILY,
    IExtraInfo,
    IFont,
    IImageEffectShadowType,
    IImageLayerProps,
    ILayerProps,
    IMarkupLayerProps,
    ImgCategoryType,
    IQrCodeLayerProps,
    ITableLayer,
    ITextLayerProps,
    IVectorLayerProps,
    LayerType,
    MaterialIdsType,
    PosterType,
    StretchOptionType,
    TextAlignType,
    TextWritingType,
} from '@meitu/saas-editor-core/es/version-types';
import { imageToBase64 } from '@meitu/saas-editor-ui/es/utils/file-kit';
import { checkTypeIsAiPPT } from '@meitu/saas-editor-ui/es/utils/stage';
import { checkFontIdIsTeamFontId, checkTeamStatusIsFree } from '@meitu/xdesign-widgets';
import { debugTemplate } from '@/debug/template';
import { editor } from '@/instance';
import { isFromSafari } from '@/utils/common';
import { findFont, findFontLostAndReplace } from '@/utils/layer';
import { getQuery } from '@/utils/query';

/**
 * 图片编辑器历史最高版本
 */
const IMAGE_EDITOR_VERSION = '8.5.0';

export const moreLineAddHalfSize = (layer: ILayerProps, oldLayer: ILayerProps) => {
    const { data } = layer;

    const result = {
        width: oldLayer.width,
        height: oldLayer.height,
    };

    if (data === null) {
        return result;
    }

    const textData = data as ITextLayerProps;
    const { lineSpacing, fontSize, writingMode } = textData;

    const lines = textData.text.split('\n').length;

    //  有换行符不算单行   图层高度超过行高 + 行高的一半算多行
    if (lines > 1) {
        return result;
    }

    // 单行
    if (
        !(
            writingMode === TextWritingType.HORIZONTAL_TB &&
            oldLayer.height >= lineSpacing * fontSize * 2 - (lineSpacing * fontSize) / 2
        ) &&
        !(
            writingMode === TextWritingType.VERTICAL_RL &&
            oldLayer.width >= lineSpacing * fontSize * 2 - (lineSpacing * fontSize) / 2
        )
    ) {
        if (writingMode === TextWritingType.HORIZONTAL_TB) {
            result.width = result.width + fontSize / 2;
        } else {
            result.height = result.height + fontSize / 2;
        }
    } else {
        // 多行
        if (
            layer.height - oldLayer.height > (lineSpacing * fontSize) / 2 &&
            writingMode === TextWritingType.HORIZONTAL_TB
        ) {
            result.width = result.width + fontSize / 2;
        } else if (
            layer.width - oldLayer.width > (lineSpacing * fontSize) / 2 &&
            writingMode === TextWritingType.VERTICAL_RL
        ) {
            result.height = result.height + fontSize / 2;
        }
    }
    return result;
};

/**
 * 修复 JSON 字符串中缺失的结束引号
 * @param {string} jsonString - 需要修复的 JSON 字符串
 * @return {string} 修复后的 JSON 字符串
 */
function fixMissingTextQuotes(jsonString: string) {
    // 使用一个临时标记替换引号转义 \"，避免干扰后续正则匹配
    const ESCAPED_QUOTE_PLACEHOLDER = '___ESCAPED_QUOTE___';
    // 先替换转义引号
    let processedString = jsonString.replace(/\\"/g, ESCAPED_QUOTE_PLACEHOLDER);
    // 处理缺失的结束引号
    processedString = processedString
        .replace(/("text"\s*:\s*")([^"]*?)(?=[,]\s*"|\})/g, '$1$2"')
        .replace(/("content"\s*:\s*")([^"]*?)(?=[,]\s*"|\})/g, '$1$2"')
        .replace(/("prompt"\s*:\s*")([^"]*?)(?=[,]\s*"|\})/g, '$1$2"');

    // 还原转义引号
    return processedString.replace(new RegExp(ESCAPED_QUOTE_PLACEHOLDER, 'g'), '\\"');
}

/** 处理文字 */
export const transferLayersToClientDoText = async (
    allLayers: ILayerProps[],
    fonts: IFont[],
    layerAndStageIndexMap: { [layerId: string]: number },
    isFromTemplate = false, // 来源官方模板
    noApplySubSetFont = false,
) => {
    const replaceFamilies: string[] = [];
    const replaceFamilyLayerIds: string[] = [];
    // ===========替换字体  调整字体大小  将服务端的数据格式转化web端的数据格式
    // 收集 字体：文字
    await asyncTraverseTrees(allLayers, async (currentLayer: ILayerProps) => {
        if (currentLayer.layerType === LayerType.TEXT_LAYER) {
            const textData = currentLayer.data as ITextLayerProps;
            // 过滤分段编辑 fontFamily 为子集的数据
            (textData.textContents || []).forEach((item) => {
                if (item.fontFamily) {
                    item.fontFamily = item.fontFamily.replace(/-[a-zA-Z0-9]{32}$/, '');
                }
            });
            const fontFamilyLost = findFontLostAndReplace(textData, fonts);

            if (fontFamilyLost.length) {
                replaceFamilies.push(...fontFamilyLost);
                const clientTextLayerData = transformLowLeverTextDataToCss(cloneDeep(textData)) as ITextLayerProps;
                const clientTextLayer = { ...currentLayer, data: clientTextLayerData };
                const textSize = TextMeasurer.measure(clientTextLayer);

                currentLayer.width = textSize.width;
                currentLayer.height = textSize.height;
                const stageIndex = layerAndStageIndexMap[currentLayer.id];
                replaceFamilyLayerIds.push(combineOriginIndex(currentLayer.id, stageIndex));
            }

            // FIXME 这里应该是判断是点击模板过来的 不是点击作图记录来的
            if (isFromTemplate && isFromSafari()) {
                const cloneLayer = cloneDeep(currentLayer);
                const clientTextLayerData = transformLowLeverTextDataToCss(cloneDeep(textData)) as ITextLayerProps;
                const clientTextLayer = { ...currentLayer, data: clientTextLayerData };

                const textSize = TextMeasurer.measure(clientTextLayer, { keepConstantWidth: true });
                cloneLayer.width = textSize.width;
                cloneLayer.height = textSize.height;
                cloneLayer.data = transformLowLeverTextDataToCss(cloneLayer.data as ITextLayerProps);
                const adjustTextSize = moreLineAddHalfSize(cloneLayer, currentLayer);

                if (
                    textData.writingMode === TextWritingType.HORIZONTAL_TB &&
                    textData.textAlign === TextAlignType.CENTER
                ) {
                    currentLayer.left = currentLayer.left - (adjustTextSize.width - currentLayer.width) / 2;
                } else if (
                    textData.writingMode === TextWritingType.HORIZONTAL_TB &&
                    textData.textAlign === TextAlignType.RIGHT
                ) {
                    currentLayer.left = currentLayer.left - (adjustTextSize.width - currentLayer.width);
                } else if (
                    textData.writingMode === TextWritingType.VERTICAL_RL &&
                    textData.textAlign === TextAlignType.CENTER
                ) {
                    currentLayer.top = currentLayer.top - (adjustTextSize.height - currentLayer.height) / 2;
                } else if (
                    textData.writingMode === TextWritingType.VERTICAL_RL &&
                    textData.textAlign === TextAlignType.RIGHT
                ) {
                    currentLayer.top = currentLayer.top - (adjustTextSize.height - currentLayer.height);
                }
                currentLayer.width = adjustTextSize.width;
                currentLayer.height = adjustTextSize.height;
            }
        }
        if (currentLayer.layerType === LayerType.TABLE) {
            const tableLayer = currentLayer as ITableLayer;
            const cells = tableLayer.data.cells.flat().filter(Boolean) as Cell[];
            const fontFamilyList = cells
                .map((c) => getCellStyle(c, tableLayer.data).fontFamily)
                .filter(Boolean) as string[];
            const lostFonts: string[] = [];
            fontFamilyList.forEach((fontFamily) => {
                const isLostFont = !findFont(fonts, fontFamily);
                if (isLostFont) {
                    lostFonts.push(fontFamily);
                }
            });
            Object.values(tableLayer.data.styles).forEach((style) => {
                if (style.fontFamily && lostFonts.includes(style.fontFamily)) {
                    style.fontFamily = DEFAULT_FONT_FAMILY().name;
                }
            });
            if (lostFonts.length) {
                replaceFamilies.push(...lostFonts);

                // 文字替换则布局可能乱，需要表格运行时自动修复
                const newTableLayer = await editor.tableLayer.fixTableLayout(tableLayer);
                // 下面代码相当于把newTableLayer赋值给tableLayer
                Object.keys(tableLayer).forEach((k) => {
                    delete tableLayer[k as keyof ITableLayer];
                });
                Object.assign(tableLayer, newTableLayer);

                const stageIndex = layerAndStageIndexMap[currentLayer.id];
                replaceFamilyLayerIds.push(combineOriginIndex(currentLayer.id, stageIndex));
            }
        }
        if (currentLayer.layerType === LayerType.MARKUP_LAYER) {
            const { label } = currentLayer.data as IMarkupLayerProps;

            const fontFamilyLost = findFontLostAndReplace(label, fonts);

            if (fontFamilyLost.length) {
                replaceFamilies.push(...fontFamilyLost);
                const stageIndex = layerAndStageIndexMap[currentLayer.id];
                replaceFamilyLayerIds.push(combineOriginIndex(currentLayer.id, stageIndex));
                fixMarkupLayerSize(currentLayer);
            }
        }
        if (currentLayer.layerType === LayerType.VECTOR_LAYER) {
            currentLayer.data = transformFoundationVectorLayerDataToClient(currentLayer.data as IVectorLayerProps);
            const { textInfo } = currentLayer.data;

            if (textInfo) {
                const fontFamilyLost = findFontLostAndReplace(textInfo, fonts);

                if (fontFamilyLost.length) {
                    replaceFamilies.push(...fontFamilyLost);
                    const stageIndex = layerAndStageIndexMap[currentLayer.id];
                    replaceFamilyLayerIds.push(combineOriginIndex(currentLayer.id, stageIndex));
                }
            }
        }
    });

    return {
        replaceFamilies: uniq(replaceFamilies),
        replaceFamilyLayerIds: uniq(replaceFamilyLayerIds),
    };
};

const getTemplate = (template: any, debug: boolean) => {
    return debug || !template
        ? debugTemplate
        : template.data?.response?.formula
          ? JSON.parse(template.data.response.formula)
          : template.data?.data.formula
            ? JSON.parse(template.data?.data.formula)
            : template;
};

export async function transferTemplateToClient(
    template: any | null,
    fonts: any,
    id: string,
    debug: boolean = false,
    isFromTemplate = false, // 来源官方模板
    noApplySubSetFont = false,
) {
    const hasOldTextMaterialLayer = false;
    const formulaJson = await getTemplate(template, debug);
    // console.log('template: ', template);

    let { preview, version, name, templateConf, unit, editMode, extraInfo } = formulaJson;

    const confIsArray = isArray(templateConf);
    if (!confIsArray) {
        templateConf = [templateConf];
    }

    const clientTemplate = {
        id,
        preview,
        version,
        name,
        templateConf,
        confIsArray,
        unit: unit || SizeUnitType.px,
        editMode,
        extraInfo,
    };

    const layerAndStageIndexMap: { [layerId: string]: number } = {};
    let allLayers: ILayerProps[] = [];

    templateConf.forEach((element: any, index: number) => {
        beforeParseServerLayersToClient(element.layers);

        const layers = parseServerLayersToClient(element, version);
        // 兼容v0模板,如果没有背景图层新增背景图层
        if (layers[0]?.layerType !== LayerType.BACKGROUND_LAYER) {
            layers.unshift(getDefaultClientBackgroundValue(element.width, element.height));
        }

        element.layers = layers;
        layers.forEach((l) => (layerAndStageIndexMap[l.id] = index));
        allLayers = allLayers.concat(layers);

        layers.forEach((item: any) => {
            traverseTree(item, (layer: any) => {
                // 记录图层在模板中的位置
                if (layer.layerType === LayerType.TEXT_LAYER) {
                    layer.stageIndex = index;
                }
            });
        });
    });

    const { replaceFamilies, replaceFamilyLayerIds } = await transferLayersToClientDoText(
        allLayers,
        fonts,
        layerAndStageIndexMap,
        isFromTemplate,
        noApplySubSetFont,
    );

    // ========  获取图层素材id materialIds
    const fontsMap: { [key: string]: number } = {};
    fonts.forEach((font: any) => {
        const key = font.font_name as string;
        fontsMap[key] = font.material_id;
    });

    const imgQrCodeLayer: ILayerProps[] = [];
    traverseTrees(allLayers, (layer: ILayerProps) => {
        if (layer.layerType === LayerType.TEXT_LAYER) {
            (layer as ILayerProps).__fixedDirectionSize = true;
            const { fontFamily, textContents } = layer.data as ITextLayerProps;
            layer.materialIds = layer.materialIds || {};
            const { materialIds } = layer;
            const allFontsMaterialIds = [
                ...new Set([fontFamily, ...(textContents?.map((e) => e.fontFamily) ?? [])]),
            ].map((font) => fontsMap[font]?.toString());

            materialIds[MaterialIdsType.FONT_FAMILY] = allFontsMaterialIds;
        }

        if (isFromTemplate && layer.layerType === LayerType.IMAGE_LAYER && layer.category === ImgCategoryType.qrCode) {
            imgQrCodeLayer.push(layer);
        }
    });

    return {
        template: clientTemplate,
        replaceFamilies,
        replaceFamilyLayerIds,
        extraInfo,
        editMode,
        hasOldTextMaterialLayer,
        allLayers,
        templateExtraInfo: formulaJson.extraInfo,
    };
}

export const transferServerMaterialLayerToClient = async (
    layerInfo: any,
    stageWidth: number,
    stageHeight: number,
    fonts: any,
) => {
    const layerAndStageIndexMap: { [layerId: string]: number } = {};
    let allLayers: ILayerProps[] = [];

    const templateConfs = [
        {
            width: stageWidth,
            height: stageHeight,
            layers: [layerInfo.layer],
        },
    ];
    templateConfs.forEach((element: any, index: number) => {
        beforeParseServerLayersToClient(element.layers);

        const layers = parseServerLayersToClient(element, layerInfo.version);

        element.layers = layers;
        layers.forEach((l) => (layerAndStageIndexMap[l.id] = index));
        allLayers = allLayers.concat(layers);

        layers.forEach((item: any) => {
            traverseTree(item, (layer: any) => {
                // 记录图层在模板中的位置
                if (layer.layerType === LayerType.TEXT_LAYER) {
                    layer.stageIndex = index;
                }
            });
        });
    });

    const fontsMap: { [key: string]: number } = {};
    fonts.forEach((font: any) => {
        const key = font.font_name as string;
        fontsMap[key] = font.material_id;
    });

    const { replaceFamilies, replaceFamilyLayerIds } = await transferLayersToClientDoText(
        allLayers,
        fonts,
        layerAndStageIndexMap,
        false,
    );

    return {
        templateConfs: templateConfs[0].layers[0],
        replaceFamilies,
        replaceFamilyLayerIds,
    };
};

export function beforeParseServerLayersToClient(layers: IServerLayerProps[], isOld = true) {
    // 兼容脏数据 (之前添加的图层是有bug导致的)
    const isDirty = (l: IServerLayerProps) => !l.layerType;
    while (layers.some(isDirty)) {
        layers.splice(layers.findIndex(isDirty), 1);
    }
    traverseTrees(layers, (layer: any) => {
        if (layer.layerType === LayerType.ADVANCED_TEXT) {
            layer.layerType = LayerType.GROUP_LAYER;
            layer.isTextMaterial = true;
        }

        if (layer.layerType === LayerType.MAGNIFIER) {
            layer.hidden = true;
        }

        // fix 配方里二维码被成组后，没有展开 data 的 bug
        if (layer.layerType === LayerType.QR_CODE) {
            if (layer.data) {
                Object.keys(layer.data).forEach((key) => {
                    layer[key] = layer.data[key];
                });
            }
        }

        // 兼容历史数据后续可删除
        if (layer.layerType === LayerType.IMAGE_LAYER) {
            const { effect } = layer;

            if (effect) {
                if (effect.shadow.length > 0 && Math.abs(effect.shadow[0].blur) > 1) {
                    effect.shadow[0].blur = effect.shadow[0].blur / 100;
                }

                if (effect.shadow.length > 0 && Math.abs(effect.shadow[0].x) > 1) {
                    effect.shadow[0].x = effect.shadow[0].x / 1000;
                }

                if (
                    effect.shadow.length > 0 &&
                    Math.abs(effect.shadow[0].y) > 1 &&
                    effect.shadow[0].type === IImageEffectShadowType.base
                ) {
                    effect.shadow[0].y = effect.shadow[0].y / 1000;
                }

                if (effect.glow.length > 0 && Math.abs(effect.glow[0].width) > 1) {
                    effect.glow[0].width = effect.glow[0].width / 100;
                }

                if (effect.stroke.length > 0 && Math.abs(effect.stroke[0].width) > 1) {
                    effect.stroke[0].width = effect.stroke[0].width / 100;
                }
            }
        }

        // 编辑器内部的layer.children是数组类型，但是规范没要求，这里加一下，防止报错
        if (!layer.children) layer.children = [];
    });
}
